import type { MouseEvent } from 'react'
import { Lock } from 'lucide-react'
import type { WireArticle } from '../../../api/types'
import type { SectionHeaderStyle } from './storyRow'
import { useWireAuth } from '../../../auth/useWireAuth'
import { isArticleLocked } from '../../../lib/articleAccess'

export interface UnrankedListConfig {
  sectionHeaderEnabled: boolean
  sectionHeaderTitle: string | null
  sectionHeaderStyle: SectionHeaderStyle
  showSummary: boolean
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

export interface UnrankedListProps {
  articles: WireArticle[]
  config?: Partial<UnrankedListConfig>
  className?: string
  onArticleClick?: (article: WireArticle, index: number, event: MouseEvent<HTMLElement>) => void
  activeArticleId?: string | null
}

const DEFAULT_CONFIG: UnrankedListConfig = {
  sectionHeaderEnabled: false,
  sectionHeaderTitle: null,
  sectionHeaderStyle: 'rule-left',
  showSummary: false,
}

function renderSectionHeader(title: string, style: SectionHeaderStyle) {
  if (style === 'all-caps') {
    return (
      <header className="mb-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-fg-secondary">{title}</h3>
      </header>
    )
  }
  if (style === 'plain') {
    return (
      <header className="mb-3">
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
      </header>
    )
  }
  // rule-left (default)
  return (
    <header className="mb-3 border-b border-line pb-1">
      <h3 className="text-xs font-bold uppercase tracking-wider text-fg-secondary">{title}</h3>
    </header>
  )
}

function UnrankedItem({
  article,
  index,
  locked,
  showSummary,
  onArticleClick,
  activeArticleId,
}: {
  article: WireArticle
  index: number
  locked?: boolean
  showSummary: boolean
  onArticleClick?: (article: WireArticle, index: number, event: MouseEvent<HTMLElement>) => void
  activeArticleId?: string | null
}) {
  const isRecalled = article.workflow?.stage === 'recalled'
  const displayTitle = isRecalled ? 'This Article Has Been Recalled' : article.title

  return (
    <article
      className={[
        'flex cursor-pointer gap-3 border-b border-line-subtle px-3 py-2.5 last:border-0 hover:bg-fill transition-all duration-200',
        activeArticleId === article.id ? 'bg-accent-primary-fill/45 ring-2 ring-inset ring-accent-primary-solid shadow-sm' : '',
        activeArticleId && activeArticleId !== article.id ? 'opacity-35 saturate-50' : '',
      ].join(' ')}
      onClick={(e) => onArticleClick?.(article, index, e)}
    >
      {!isRecalled && article.thumbnail?.presignedUrl ? (
        <img
          src={article.thumbnail.presignedUrl}
          alt=""
          aria-hidden="true"
          className="h-14 w-20 shrink-0 object-cover"
          style={article.thumbnail.focalPoint
            ? { objectPosition: `${article.thumbnail.focalPoint.x * 100}% ${article.thumbnail.focalPoint.y * 100}%` }
            : undefined}
        />
      ) : locked ? (
        <div className="flex h-14 w-20 shrink-0 items-center justify-center bg-fill">
          <Lock size={28} className="text-warning-fg" aria-hidden="true" />
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <h4 className="font-zodiak text-sm font-bold leading-snug text-fg">
          {locked && <Lock size={11} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}
          ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {displayTitle}
        </h4>
        {!isRecalled && showSummary && article.description ? (
          <p className="mt-1 text-xs leading-relaxed text-fg-secondary">{article.description}</p>
        ) : null}
        <p className="mt-1 text-[9px] uppercase tracking-wide text-fg-secondary">
          {article.regions?.[0]} · {formatDate(article.publishedAt ?? article.updatedAt)}
        </p>
      </div>
    </article>
  )
}

export function UnrankedList({ articles, config, className, onArticleClick, activeArticleId }: UnrankedListProps) {
  const { user } = useWireAuth()
  const merged = { ...DEFAULT_CONFIG, ...config }

  return (
    <section className={className}>
      {merged.sectionHeaderEnabled && merged.sectionHeaderTitle
        ? renderSectionHeader(merged.sectionHeaderTitle, merged.sectionHeaderStyle)
        : null}

      <div>
        {articles.map((article, index) => (
          <UnrankedItem
            key={article.id}
            article={article}
            index={index}
            locked={Boolean(user && isArticleLocked(article, user))}
            showSummary={merged.showSummary}
            onArticleClick={onArticleClick}
            activeArticleId={activeArticleId}
          />
        ))}
      </div>
    </section>
  )
}
